import { supabase } from '../lib/supabase';

/**
 * Service for managing notifications
 */
export const notificationService = {
  /**
   * Get all notifications for a user
   */
  async getNotifications(userId, filters = {}) {
    let query = supabase
      ?.from('notifications')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('priority', { ascending: false })
      ?.order('created_at', { ascending: false });

    // Apply filters
    if (filters?.type && filters?.type !== 'all') {
      query = query?.eq('type', filters?.type);
    }

    if (filters?.status === 'unread') {
      query = query?.eq('is_read', false);
    } else if (filters?.status === 'read') {
      query = query?.eq('is_read', true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount(userId) {
    const { count, error } = await supabase
      ?.from('notifications')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('user_id', userId)
      ?.eq('is_read', false);

    if (error) throw error;
    return count || 0;
  },

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true })
      ?.eq('id', notificationId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Mark multiple notifications as read
   */
  async markMultipleAsRead(notificationIds) {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true })
      ?.in('id', notificationIds)
      ?.select();

    if (error) throw error;
    return data;
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(userId) {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true })
      ?.eq('user_id', userId)
      ?.eq('is_read', false)
      ?.select();

    if (error) throw error;
    return data;
  },

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    const { error } = await supabase
      ?.from('notifications')
      ?.delete()
      ?.eq('id', notificationId);

    if (error) throw error;
  },

  /**
   * Delete multiple notifications
   */
  async deleteMultiple(notificationIds) {
    const { error } = await supabase
      ?.from('notifications')
      ?.delete()
      ?.in('id', notificationIds);

    if (error) throw error;
  },

  /**
   * Create a new notification
   */
  async createNotification(userId, notificationData) {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.insert({
        user_id: userId,
        type: notificationData?.type,
        title: notificationData?.title,
        message: notificationData?.message,
        category_color: notificationData?.categoryColor,
        priority: notificationData?.priority || false,
        action_label: notificationData?.actionLabel,
        action_path: notificationData?.actionPath
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Subscribe to real-time notification changes
   */
  subscribeToNotifications(userId, callback) {
    const channel = supabase
      ?.channel('notifications_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.subscribe();

    return channel;
  }
};

export default notificationService;